import { useEffect, useRef, useState } from "react";

// "How Revetra works" — three looping product demos, one per step of
// the flow. Self-contained (no store, no route deps) so it can be
// dropped onto any marketing page. Videos live in /public/videos and
// are served as static assets; swap SOURCES if they move to a CDN.
interface Step {
  ord: string;
  title: string;
  src: string;
}

const STEPS: Step[] = [
  {
    ord: "1",
    title: "Upload Your Denial Letter",
    src: "/videos/revetra-step-1.mp4",
  },
  {
    ord: "2",
    title: "Understand Your Results",
    src: "/videos/revetra-step-2.mp4",
  },
  {
    ord: "3",
    title: "Generate Your Appeal",
    src: "/videos/revetra-step-3.mp4",
  },
];

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

// Resolved in an effect rather than during render. The preference is a
// browser-only signal, so reading it while rendering both breaks the
// build-time prerender (no `window`) and desynchronises hydration on a
// prerendered page, because it decides whether `controls` appears in the
// markup.
//
// `undefined` means "not yet known" and is deliberately distinct from
// `false`. The autoplay effect below refuses to attach its observer
// while the answer is unknown, so a reduced-motion visitor never gets a
// frame of autoplay in the window before the preference resolves.
function useReducedMotion(): boolean | undefined {
  const [reduced, setReduced] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      setReduced(false);
      return;
    }
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

// Muted looping demo that plays only while on screen. Users who
// prefer reduced motion get a paused player with controls instead.
function StepVideo({ step }: { step: Step }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const reducedMotion = useReducedMotion();

  // `muted` is set imperatively, not as a JSX prop. React does not
  // serialize it during server rendering, so the prerendered HTML would
  // lack the attribute while the client expects it — a hydration mismatch
  // that tears down the whole page. It has to be set before play() either
  // way, since browsers block unmuted autoplay.
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = true;
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    // `undefined` = preference not yet resolved; stay inert until it is.
    if (!video || reducedMotion !== false) return;
    if (!("IntersectionObserver" in window)) {
      void video.play().catch(() => {});
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            void video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.35 },
    );
    io.observe(video);
    return () => io.disconnect();
  }, [reducedMotion]);

  return (
    <video
      ref={videoRef}
      src={step.src}
      loop
      playsInline
      preload="metadata"
      controls={reducedMotion === true}
      className="aspect-video w-full rounded-xl border border-line bg-parchment-deep object-cover shadow-sm"
      aria-label={`Demo: ${step.title}`}
    />
  );
}

export function HowRevetraWorks() {
  return (
    <section className="bg-parchment px-6 py-24 max-[980px]:py-16 sm:px-8" aria-labelledby="how-revetra-works">
      <div className="mx-auto max-w-[880px]">
        <div className="lp-reveal text-center">
          <span className="eyebrow">The process</span>
          <h2
            id="how-revetra-works"
            className="mt-6 font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-normal leading-[1.1] tracking-[-0.018em] text-ink"
          >
            How Revetra works
          </h2>
          <p className="mt-4 text-[16px] leading-[1.65] text-ink-soft sm:text-[17px]">
            It only takes three simple steps.
          </p>
        </div>

        <ol className="mt-14 max-[980px]:mt-10">
          {STEPS.map((step, i) => (
            <li
              key={step.ord}
              className={
                "lp-reveal py-12 max-[980px]:py-9" +
                (i > 0 ? " border-t-[0.5px] border-line" : "")
              }
            >
              <div className="mb-6 flex items-center justify-center gap-3.5">
                <span
                  aria-hidden
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ink font-serif text-[17px] leading-none text-ink"
                >
                  {step.ord}
                </span>
                <h3 className="font-serif text-[22px] font-normal tracking-[-0.012em] text-ink max-[980px]:text-[19px]">
                  <span className="sr-only">Step {step.ord}: </span>
                  {step.title}
                </h3>
              </div>
              <StepVideo step={step} />
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
